import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import {
  PrismButton,
  PrismCard,
  PrismTooltip,
} from '@mylighthouse/prism-ember';
import type { Dayjs } from 'dayjs';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import {
  BENI_COMPARISON_SETS,
  type ComparisonSet,
} from 'frontend-utils/utils/constants/benchmark-insight';
import SkeletonTableIllustration from 'lumen/assets/images/benchmark-insight/skeleton/skeleton-table.svg?raw';
import HotelBenchmarkInsightSectionsSectionSegmentSectionSegmentOverlay from 'lumen/components/hotel/benchmark-insight/sections/section-segment/section-segment-overlay';
import HotelBenchmarkInsightSkeleton from 'lumen/components/hotel/benchmark-insight/skeleton';
import HotelBenchmarkInsightTablesBeniTable from 'lumen/components/hotel/benchmark-insight/tables/beni-table';
import HotelBenchmarkInsightUpsellIcon from 'lumen/components/hotel/benchmark-insight/upsell-icon';
import type {
  BeniBreakdown,
  BeniMeta,
  BeniMetaComparisonSet,
} from 'lumen/models';
import {
  ALL_SEGMENTS_OPTION,
  BENI_SEGMENT_FULL_ID,
  BREAKDOWNS,
  isKpiCategory,
  KPIS,
  type ReferencePeriod,
} from 'lumen/utils/constants/benchmark-insight/beni-constant-values';
import {
  BENI_TABLE_ROW_TYPES,
  type Column,
  type SegmentItem,
} from 'lumen/utils/constants/benchmark-insight/beni-table-constant-values';
import type { DateAggregationType } from 'lumen/utils/constants/general-constants';
import type MappingCategoryBenchmark from 'lumen/utils/datamodels/mapping-category-benchmark';

import styles from './section-segment.module.css';

interface HotelBenchmarkInsightSectionsSectionSegmentSectionSegmentSignature {
  Args: {
    breakdownsByComparisonSet: Record<ComparisonSet, BeniBreakdown[]>;
    columns: Column[];
    dateAggregation: DateAggregationType;
    meta: BeniMeta;
    primaryComparisonSetType: ComparisonSet;
    referencePeriod: ReferencePeriod;
    rootCategory: MappingCategoryBenchmark;
    rows: SegmentItem[];
    secondaryComparisonSetType: ComparisonSet;
    segmentItems: SegmentItem[];
    selectedDateRange: string;
    selectedRange: {
      end: Dayjs;
      start: Dayjs;
    };
    selectedSegment: string;
    showSkeleton: boolean;
    showSkeletonAnimation: boolean;
  };
}

export default class HotelBenchmarkInsightSectionsSectionSegmentSectionSegment extends Component<HotelBenchmarkInsightSectionsSectionSegmentSectionSegmentSignature> {
  @service('benchmark-insight/beni-table-service')
  declare benchmarkInsightBeniTableService: Services['benchmark-insight/beni-table-service'];
  @service('benchmark-insight/features')
  declare benchmarkInsightFeatures: Services['benchmark-insight/features'];
  @service declare errorTracking: Services['error-tracking'];
  @service declare intl: Services['intl'];
  @service declare usageAnalytics: Services['usage-analytics'];

  breakdown = BREAKDOWNS.SEGMENT;

  exportSegmentTableTask = task({}, async (): Promise<void> => {
    const { rootCategory, selectedRange } = this.args;
    const { columns, rows, breakdown } = this;

    await this.benchmarkInsightBeniTableService.exportTableTask.perform({
      columns,
      rows,
      breakdown,
      rootCategory,
      dateRange: selectedRange,
    });

    this.usageAnalytics.track('Table Exported', {
      eventTrigger: 'segmentation',
      product: 'benchmark-insight',
    });
  });

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get columns() {
    const columns = this.benchmarkInsightBeniTableService.getColumns({
      primaryComparisonSetType: this.args.primaryComparisonSetType,
      secondaryComparisonSetType: this.args.secondaryComparisonSetType,
      ownComparisonSetMeta: this.ownComparisonSetMeta,
      primaryComparisonSetMeta: this.primaryComparisonSetMeta,
      secondaryComparisonSetMeta: this.secondaryComparisonSetMeta,
      breakdown: this.breakdown,
      dateAggregation: this.args.dateAggregation,
      selectedRange: this.args.selectedRange,
      referencePeriod: this.args.referencePeriod,
      visibleKpis: this.visibleKpis,
    });

    return columns;
  }

  /**
   * Checks whether the hierarchy data is available then flattens all the segments in the root BenchmarkMappingCategory object into a list
   * @return { Array } : List of segments
   */
  get flattenedCategories(): MappingCategoryBenchmark[] {
    const flattenedCategories: MappingCategoryBenchmark[] = [];

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const traverseCategories = (category: MappingCategoryBenchmark) => {
      flattenedCategories.push(category);
      category.children.forEach(traverseCategories);
    };

    traverseCategories(this.args.rootCategory);

    return flattenedCategories;
  }

  get isExportButtonDisabled(): boolean {
    return (
      this.args.showSkeleton ||
      this.exportSegmentTableTask.isRunning ||
      this.benchmarkInsightFeatures.isBenchmarkInsightLight
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get ownComparisonSetBreakdowns() {
    const { breakdownsByComparisonSet } = this.args;

    const ownBreakdowns = this.getComparisonSetBreakdowns(
      breakdownsByComparisonSet,
      BENI_COMPARISON_SETS.OWN,
    );
    return this.filterBreakdowns(ownBreakdowns);
  }

  get ownComparisonSetMeta(): BeniMetaComparisonSet | null {
    const { meta } = this.args;

    if (!meta) {
      return null;
    }

    return meta.getMetaForComparisonSet(BENI_COMPARISON_SETS.OWN);
  }

  // This function turns a flat list of rows into a nested tree structure,
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get primaryComparisonSetBreakdowns() {
    const { breakdownsByComparisonSet, primaryComparisonSetType } = this.args;

    const primaryComparisonSetBreakdowns = this.getComparisonSetBreakdowns(
      breakdownsByComparisonSet,
      primaryComparisonSetType,
    );
    return this.filterBreakdowns(primaryComparisonSetBreakdowns);
  }

  get primaryComparisonSetMeta(): BeniMetaComparisonSet | null {
    const { meta, primaryComparisonSetType } = this.args;

    if (!meta || !primaryComparisonSetType) {
      return null;
    }

    return meta.getMetaForComparisonSet(primaryComparisonSetType);
  }

  get rows(): SegmentItem[] {
    const { segmentItems } = this;
    const rows = this.benchmarkInsightBeniTableService.getRows(
      this.ownComparisonSetBreakdowns,
      this.primaryComparisonSetBreakdowns,
      this.secondaryComparisonSetBreakdowns,
      segmentItems,
      this.breakdown,
    );

    const nestedRows = this.nestRows(rows);

    const totalRow = rows.find((row) => row.isTotalRow);
    if (!totalRow) {
      return nestedRows;
    }

    return [...nestedRows, totalRow];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get secondaryComparisonSetBreakdowns() {
    const { breakdownsByComparisonSet, secondaryComparisonSetType } = this.args;

    const secondaryComparisonSetBreakdowns = this.getComparisonSetBreakdowns(
      breakdownsByComparisonSet,
      secondaryComparisonSetType,
    );
    return this.filterBreakdowns(secondaryComparisonSetBreakdowns);
  }

  get secondaryComparisonSetMeta(): BeniMetaComparisonSet | null {
    const { meta, secondaryComparisonSetType } = this.args;

    if (!meta || !secondaryComparisonSetType) {
      return null;
    }

    return meta.getMetaForComparisonSet(secondaryComparisonSetType);
  }

  get segmentItems(): SegmentItem[] {
    return this.flattenedCategories.map((category) => {
      const categoryName = category.getDisplayName(
        this.intl,
        this.errorTracking,
      );

      const id = category.id;

      return {
        id,
        value: categoryName ?? undefined,
        isParent: category.parentId === null,
        order: isKpiCategory(id)
          ? this.args.meta.allSegments.indexOf(id) + 1
          : undefined,
        depth: category.depth,
      };
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showSkeleton() {
    // Show skeleton when loading (`this.args.showSkeleton`) or when the own PMS integration is not set up yet (`this.showSegmentationNotAvailableOverlay`).
    return (
      this.args.showSkeleton ||
      this.benchmarkInsightFeatures.showSegmentationNotAvailableOverlay
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showSkeletonAnimation() {
    return (
      this.args.showSkeletonAnimation &&
      !this.benchmarkInsightFeatures.showSegmentationNotAvailableOverlay
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get visibleKpis() {
    return Object.values(KPIS).filter((kpi) => kpi !== KPIS.OCCUPANCY_MIX);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  filterBreakdowns(breakdowns: BeniBreakdown[]) {
    return breakdowns.filter((breakdown) => {
      const isSegmentationCategory = Object.values(
        BENI_SEGMENT_FULL_ID,
      ).includes(breakdown.label);
      const isTotalItem = breakdown.label.includes(BENI_TABLE_ROW_TYPES.TOTAL);

      return isSegmentationCategory || isTotalItem;
    });
  }

  getComparisonSetBreakdowns(
    breakdownsByComparisonSet: Partial<Record<ComparisonSet, BeniBreakdown[]>>,
    comparisonSet: ComparisonSet,
  ): BeniBreakdown[] {
    if (!breakdownsByComparisonSet) {
      return [];
    }

    const comparisonSetBreakdowns = breakdownsByComparisonSet[comparisonSet];
    if (!comparisonSetBreakdowns) {
      return [];
    }

    return comparisonSetBreakdowns;
  }

  // by mimicking the hierarchy of the mapping categories.
  nestRows(rows: SegmentItem[]): SegmentItem[] {
    const { rootCategory, selectedSegment } = this.args;

    // Recursive function to map the hierarchy of mapping categories to the nested rows.
    const mapHierarchy = (
      category: MappingCategoryBenchmark,
      rowsByCategoryId: Record<string, SegmentItem>,
    ): SegmentItem => {
      return {
        ...rowsByCategoryId[category.id],
        children: category.children.map((child) =>
          mapHierarchy(child, rowsByCategoryId),
        ),
      };
    };

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const rowsByCategoryId: Record<string, SegmentItem> = Object.fromEntries(
      rows.map((row) => [row.categoryId, row]),
    );

    let rootCategoryToUse = rootCategory;

    if (selectedSegment !== ALL_SEGMENTS_OPTION.id) {
      // Recursively find the category in the hierarchy that matches the selected segment.
      const findCategory = (
        category: MappingCategoryBenchmark,
      ): MappingCategoryBenchmark | null => {
        if (category.id === selectedSegment) {
          return category;
        }

        for (const child of category.children) {
          const foundCategory = findCategory(child);
          if (foundCategory) {
            return foundCategory;
          }
        }

        return null;
      };

      const foundCategory = findCategory(rootCategory);
      if (foundCategory) {
        rootCategoryToUse = foundCategory;
      }
    }

    const rootRow = mapHierarchy(rootCategoryToUse, rowsByCategoryId);

    if (selectedSegment === ALL_SEGMENTS_OPTION.id) {
      // We don't want to show the root category in the table, so we return its children.
      return rootRow['children'] ?? [];
    }

    return [rootRow];
  }

  <template>
    <PrismCard
      @contentLayout="noPadding"
      class="{{styles.container}}
        ic-product-tour-trial-onboarding-performance-segmentation"
    >
      <:header>
        <div
          class="flex items-center"
          data-test-id="benchmark-insight-section-segment-header"
        >
          {{#if this.benchmarkInsightFeatures.showTrialTeasers}}
            <div class="mr-200">
              <HotelBenchmarkInsightUpsellIcon
                @tooltipText={{this.benchmarkInsightFeatures.beniLightSegmentationTooltipText}}
              />
            </div>
          {{/if}}

          <p class="text-400--bold">
            {{t "benchmark_insight.headers.detailed_segmentation_overview"}}
          </p>
        </div>

      </:header>

      <:headerAfter>
        <div class="flex justify-center">
          {{#if this.benchmarkInsightFeatures.showExportButtons}}
            <div>
              <PrismButton
                @buttonType="secondary"
                @iconBefore="export"
                @isDisabled={{this.isExportButtonDisabled}}
                @isLoading={{this.exportSegmentTableTask.isRunning}}
                @label={{t "general.export_group.export"}}
                @onClick={{this.exportSegmentTableTask.perform}}
                class="text-nowrap"
                data-test-id="section-segment-export-button"
              />

              {{#if this.benchmarkInsightFeatures.isBenchmarkInsightLight}}
                <PrismTooltip
                  @text={{this.benchmarkInsightFeatures.beniLightSegmentationTooltipText}}
                />
              {{/if}}
            </div>
          {{/if}}
        </div>
      </:headerAfter>

      <:content>
        <div class={{styles.card-content}}>
          {{#if this.showSkeleton}}
            <div class={{styles.skeleton}}>
              <HotelBenchmarkInsightSkeleton
                @imageHtml={{SkeletonTableIllustration}}
                @showAnimation={{this.showSkeletonAnimation}}
              />

              {{#if
                this.benchmarkInsightFeatures.showSegmentationNotAvailableOverlay
              }}
                <HotelBenchmarkInsightSectionsSectionSegmentSectionSegmentOverlay
                />
              {{/if}}
            </div>
          {{else}}
            <HotelBenchmarkInsightTablesBeniTable
              @columns={{this.columns}}
              @dataTestId="beni-segmentation-table"
              @removeBorderRadius={{true}}
              @rows={{this.rows}}
            />
          {{/if}}
        </div>
      </:content>
    </PrismCard>
  </template>
}
